import { __rest } from "tslib";
import { roundDec, sanitizeString } from "@tedivo/tedivo-pure-helpers";
import createGridPart from "./createGridPart";
/**
 * Generates a SVG Grid (Above & Below) with dimensions
 * @returns ISvgWithSize {svg, width, height}
 */
export default function createBayGrid(_a) {
    var _b, _c, _d;
    var { aboveTiersArray, belowTiersArray, belowGridSeparation = 0, lidData, aboveTcgLabelsByRow, belowTcgLabelsByRow, rowsArray, aboveRowsArray = rowsArray, belowRowsArray = rowsArray } = _a, commonGridOptions = __rest(_a, ["aboveTiersArray", "belowTiersArray", "belowGridSeparation", "lidData", "aboveTcgLabelsByRow", "belowTcgLabelsByRow", "rowsArray", "aboveRowsArray", "belowRowsArray"]);
    const svgGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
    const aboveSvg = aboveTiersArray.length
        ? createGridPart(Object.assign(Object.assign({}, commonGridOptions), { rowsArray: aboveRowsArray, tiersArray: aboveTiersArray.slice().sort(sortNumericDesc), displayNumbers: { left: true, right: true, top: true }, tcgLabelsByRow: aboveTcgLabelsByRow }))
        : undefined;
    const belowSvg = belowTiersArray.length
        ? createGridPart(Object.assign(Object.assign({}, commonGridOptions), { rowsArray: belowRowsArray, tiersArray: belowTiersArray.slice().sort(sortNumericDesc), displayNumbers: { left: true, right: true, bottom: true }, tcgLabelsByRow: belowTcgLabelsByRow }))
        : undefined;
    // Parts can differ in width (one of them without the centerline row): center the narrower one
    const width = Math.max((_b = aboveSvg === null || aboveSvg === void 0 ? void 0 : aboveSvg.width) !== null && _b !== void 0 ? _b : 0, (_c = belowSvg === null || belowSvg === void 0 ? void 0 : belowSvg.width) !== null && _c !== void 0 ? _c : 0);
    const aboveHeight = (_d = aboveSvg === null || aboveSvg === void 0 ? void 0 : aboveSvg.height) !== null && _d !== void 0 ? _d : 0;
    const belowOffsetY = aboveHeight > 0 ? aboveHeight + belowGridSeparation : 0;
    const symbols = {};
    const cellPositions = {};
    if (aboveSvg) {
        placeGridPart(aboveSvg, (width - aboveSvg.width) * 0.5, 0, cellPositions);
        svgGroup.appendChild(aboveSvg.svgGroup);
        Object.assign(symbols, aboveSvg.symbols);
    }
    if (belowSvg) {
        placeGridPart(belowSvg, (width - belowSvg.width) * 0.5, belowOffsetY, cellPositions);
        svgGroup.appendChild(belowSvg.svgGroup);
        Object.assign(symbols, belowSvg.symbols);
    }
    const height = belowSvg ? belowOffsetY + belowSvg.height : aboveHeight;
    const nodesOptions = commonGridOptions.nodesOptions;
    const lidsPath = drawLids({
        lidData,
        yPos: aboveHeight ? aboveHeight + (belowGridSeparation || 0) * 0.5 : 0,
        containerWidth: commonGridOptions.containerWidth,
        lidHeight: commonGridOptions.lidHeight,
        // Lids cover the hold: below-deck cells are added last, so their row positions win
        cellPositions,
        lidBorderColor: nodesOptions.lidBorderColor,
        lidBackgroundColor: nodesOptions.lidBackgroundColor,
        lidDangerBorderColor: nodesOptions.lidDangerBorderColor,
        lidDangerBackgroundColor: nodesOptions.lidDangerBackgroundColor,
        lidWarningBorderColor: nodesOptions.lidWarningBorderColor,
        lidWarningBackgroundColor: nodesOptions.lidWarningBackgroundColor,
        lidTextColor: nodesOptions.emphasizeLineColor,
        cellsAreSeparated: commonGridOptions.cellSeparation !== undefined && commonGridOptions.cellSeparation > 0,
    });
    if (lidsPath)
        svgGroup.appendChild(lidsPath);
    return {
        svgGroup,
        width,
        height,
        symbols,
        cellPositions,
    };
}
/** Moves a grid part to (dx, dy) and adds its offset cell positions to `target` */
function placeGridPart(part, dx, dy, target) {
    if (dx || dy)
        part.svgGroup.setAttribute("transform", `translate(${dx}, ${dy})`);
    Object.keys(part.cellPositions).forEach((pos) => {
        target[pos] = {
            x: part.cellPositions[pos].x + dx,
            y: part.cellPositions[pos].y + dy,
        };
    });
}
function sortNumericDesc(a, b) {
    return Number(b) - Number(a);
}
function drawLids({ lidData, yPos, containerWidth, lidHeight, cellPositions, lidBorderColor, lidBackgroundColor, lidDangerBorderColor, lidDangerBackgroundColor, lidWarningBorderColor, lidWarningBackgroundColor, lidTextColor, cellsAreSeparated, }) {
    if (lidData === undefined || lidData === null || lidData.length === 0)
        return null;
    const LIDHEIGHT = lidHeight;
    const OVERLAP_WIDTH = roundDec(containerWidth * 0.15, 3);
    const OVERLAP_HEIGHT = roundDec(LIDHEIGHT * (cellsAreSeparated ? 0.42 : 0.5), 3);
    const rowPositions = {};
    Object.keys(cellPositions).forEach((pos) => {
        const [row] = pos.split("|");
        rowPositions[row] = cellPositions[pos].x;
    });
    const svgLidsPath = document.createElementNS("http://www.w3.org/2000/svg", "g");
    const lidDataSorted = lidData
        .slice()
        .sort((a, b) => (rowPositions[a.portIsoRow] < rowPositions[b.portIsoRow] ? -1 : 1));
    const maxLidIndex = lidDataSorted.length - 1;
    // Important. Because of sorting, we draw from Left to Right. PrevLid will be at the left always.
    lidDataSorted.forEach((lid, idx) => {
        const { svgPath, svgLabel } = addLidSvgPathInfo({
            lid,
            yOffset: yPos - LIDHEIGHT * 0.5,
            prevLid: idx > 0 ? lidDataSorted[idx - 1] : undefined,
            nextLid: idx < maxLidIndex ? lidDataSorted[idx + 1] : undefined,
        });
        svgLidsPath.appendChild(svgPath);
        svgLidsPath.appendChild(svgLabel);
    });
    return svgLidsPath;
    function addLidSvgPathInfo({ lid, yOffset, prevLid, nextLid, }) {
        const svgLidPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
        const left = rowPositions[lid.portIsoRow];
        const right = rowPositions[lid.starboardIsoRow];
        const leftTop = lid.overlapPort
            ? `M${left},${OVERLAP_HEIGHT + yOffset} h-${OVERLAP_WIDTH} v-${OVERLAP_HEIGHT}`
            : (prevLid === null || prevLid === void 0 ? void 0 : prevLid.overlapStarboard)
                ? `M${left + OVERLAP_WIDTH},${yOffset}`
                : `M${left},${yOffset}`;
        const rightTop = lid.overlapStarboard
            ? `L${right + containerWidth + OVERLAP_WIDTH},${yOffset} v${OVERLAP_HEIGHT} h-${OVERLAP_WIDTH} `
            : (nextLid === null || nextLid === void 0 ? void 0 : nextLid.overlapPort)
                ? `L${right + containerWidth - OVERLAP_WIDTH},${yOffset} V${yOffset + LIDHEIGHT - OVERLAP_HEIGHT} h${OVERLAP_WIDTH}`
                : `L${right + containerWidth},${yOffset}`;
        const rightBottom = `L${right + containerWidth},${yOffset + LIDHEIGHT}`;
        const leftBottom = (prevLid === null || prevLid === void 0 ? void 0 : prevLid.overlapStarboard)
            ? `L${left},${yOffset + LIDHEIGHT} v-${OVERLAP_HEIGHT} h${OVERLAP_WIDTH}`
            : `L${left},${yOffset + LIDHEIGHT}`;
        const d = [leftTop, rightTop, rightBottom, leftBottom, "z"].join("\n");
        const borderColor = lid.notValid === "danger"
            ? lidDangerBorderColor || "red"
            : lid.notValid === "warning"
                ? lidWarningBorderColor || "orange"
                : lidBorderColor || "black";
        const backgroundColor = lid.notValid === "danger"
            ? lidDangerBackgroundColor || "red"
            : lid.notValid === "warning"
                ? lidWarningBackgroundColor || "orange"
                : lidBackgroundColor || "black";
        svgLidPath.setAttribute("d", d);
        svgLidPath.setAttribute("fill", backgroundColor);
        svgLidPath.setAttribute("fill-opacity", "0.5");
        svgLidPath.setAttribute("stroke", borderColor);
        const svgTitle = document.createElementNS("http://www.w3.org/2000/svg", "title");
        svgTitle.textContent = sanitizeString(lid.label);
        svgLidPath.appendChild(svgTitle);
        svgLidPath.setAttribute("part", "svg-lid");
        svgLidPath.setAttribute("role", "figure");
        const svgLabel = document.createElementNS("http://www.w3.org/2000/svg", "text");
        svgLabel.setAttribute("x", `${left + (right + containerWidth - left) * 0.5}`);
        svgLabel.setAttribute("y", `${yOffset + LIDHEIGHT * 0.55}`);
        svgLabel.setAttribute("text-anchor", "middle");
        svgLabel.setAttribute("dominant-baseline", "middle");
        svgLabel.setAttribute("font-size", `${Math.min(LIDHEIGHT * 0.85, 14)}`);
        svgLabel.setAttribute("fill", lidTextColor || "black");
        svgLabel.setAttribute("part", "svg-lid-label");
        svgLabel.setAttribute("role", "text");
        svgLabel.setAttribute("pointer-events", "none");
        svgLabel.setAttribute("font-family", "Arial, Helvetica, sans-serif");
        svgLabel.textContent = sanitizeString(lid.label);
        return { svgPath: svgLidPath, svgLabel };
    }
}
